/**
 * OFFBOARDING → HEBREW. What happens when a customer leaves, in words an operator can act on.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE ONE THING THIS MODULE EXISTS TO PREVENT: "אפשר לבטל / אי אפשר לבטל"
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `apps/api/src/services/tenant-lifecycle.ts` models reversibility as a UNION and says why: *"A boolean
 * cannot carry the one thing a caller must know before acting: what UNDOING this costs."* The five
 * members are five different conversations, and collapsing them is not a cosmetic loss:
 *
 *   FREE             nothing was withdrawn. Reversal is one row, and there is nothing to restore.
 *   GOVERNED_WINDOW  reversal is permitted UNTIL A STATED INSTANT, and it restores NOTHING — the
 *                    relationship comes back, the access does not. Both halves are on screen.
 *   CLOSED           the window has passed. Nothing has been destroyed YET, and that distinction is
 *                    the whole difference between this and `IRREVERSIBLE`.
 *   UNDETERMINABLE   there is no readable governing policy, so the answer is NOT KNOWN. This is the
 *                    member a boolean would render as "cannot be undone", and an operator who reads
 *                    that about a customer terminated in error re-creates them as a new tenant —
 *                    losing the audit thread, the identity graph and the conflict history that a
 *                    reinstatement would have kept. It says WHY, and it says the answer is unknown.
 *   IRREVERSIBLE     the data is gone. The only member where that sentence is true.
 *
 * ── AND THE SENTENCE THE WHOLE SURFACE TURNS ON ─────────────────────────────────────────────────
 *
 * **TERMINATION IS NOT DELETION.** The service destroys nothing — its persistence port has no
 * `delete*`, no `purge*`, no `drop*` and no `archive*` method — and an operator who believes
 * otherwise makes one of two expensive mistakes: they avoid a correct action for months, or they
 * take a catastrophic one believing it is reversible. `TERMINATION_REVOKES` names each of the five
 * things that DO stop, in the order they stop, and `TERMINATION_DESTROYS_NOTHING_HE` says the rest.
 *
 * ── WHAT THIS MODULE DOES NOT DO ────────────────────────────────────────────────────────────────
 *
 * It decides nothing. It never sees a capability, a role or a session; every verdict below arrived
 * already made by the server. In particular `purgeText` renders an ANSWER and offers no action,
 * because there is no purge route and no capability that could authorise one.
 *
 * Pure, and therefore provable in a plain node process — `offboardingText.test.ts` beside it.
 */

import type {
  ExportDispositionKind,
  MustReestablishItem,
  OffboardingRefusal,
  OffboardingStage,
  OffboardingStageName,
  PurgeBlocker,
  PurgeEligibility,
  ResidencyCategory,
  RetentionPolicySummary,
  RetentionPolicyViolation,
  Reversal,
  ReversalKind,
  RevocationClass,
  RevocationTally,
} from './contract';
import {
  OFFBOARDING_STAGES,
  RESIDENCY_CATEGORIES,
  REVOCATION_CLASSES,
} from './contract';
import type { ConflictBody } from './client';
import type { Tone } from './adminVocabulary';
import { formatInstantHe } from './adminVocabulary';
import { tenantLifecycleRefusalText } from './lifecycleText';

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE STAGE
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export interface StageTermHe {
  readonly labelHe: string;
  readonly tone: Tone;
  /** What this stage MEANS for the customer, not what the column says. */
  readonly explanationHe: string;
}

const STAGE_HE: Readonly<Record<OffboardingStageName, StageTermHe>> = {
  ACTIVE: {
    labelHe: 'התקשרות פעילה',
    tone: 'ok',
    explanationHe:
      'המשרד הזה לא נכנס לתהליך סיום. אין חלון החזרה למנות ואין שעון שמירה שרץ, משום שלא נשלל דבר.',
  },
  SUSPENDED: {
    labelHe: 'מושהה — לא נשלל דבר',
    tone: 'warn',
    explanationHe:
      'השעיה חוסמת פעולות ואינה שוללת גישה: אישורי החיבור, ההרשאות למקורות, ההזמנות וההתחברויות ' +
      'נשארו כפי שהיו. לכן הביטול שלה אינו עולה דבר, והוא נעשה ממחזור החיים המסחרי ולא מכאן.',
  },
  TERMINATED: {
    labelHe: 'ההתקשרות הסתיימה',
    tone: 'error',
    explanationHe:
      'הגישה נשללה במלואה והנתונים לא נמחקו. מכאן ואילך השאלה היחידה היא עד מתי אפשר עוד להחזיר את ' +
      'ההתקשרות, ומתי מותר יהיה לתהליך המחיקה המבוקר להתחיל — שתי שאלות שונות עם שני מועדים.',
  },
  PURGED: {
    labelHe: 'הנתונים הושמדו',
    tone: 'muted',
    explanationHe:
      'תהליך המחיקה המבוקר כבר רץ על המשרד הזה. זה המצב היחיד שממנו אין חזרה, והוא כזה משום שאין ' +
      'עוד מה להחזיר — ולא משום שההחזרה אסורה.',
  },
};

export function stageHe(stage: OffboardingStageName): StageTermHe {
  return STAGE_HE[stage];
}

/** Exposed for the mirror test only — a stage with no Hebrew is an English enum on a Hebrew screen. */
export function mappedStages(): readonly string[] {
  return Object.keys(STAGE_HE);
}

/** When the tenant entered the stage it is in. `null` on `ACTIVE`, which was never entered. */
export function stageEnteredHe(stage: OffboardingStage): string | null {
  return stage.stage === 'ACTIVE' ? null : formatInstantHe(stage.enteredAt);
}

/** The written reason recorded with the transition. `null` on `ACTIVE`, and on a blank reason. */
export function stageReasonHe(stage: OffboardingStage): string | null {
  if (stage.stage === 'ACTIVE') return null;
  return stage.reason.trim() === '' ? null : stage.reason;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// WHAT UNDOING COSTS — five members, five answers
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export interface ReversalText {
  readonly kind: ReversalKind;
  readonly labelHe: string;
  readonly tone: Tone;
  /** What reversal costs FROM HERE. Never "yes/no". */
  readonly explanationHe: string;
  /** The stated instant reversal is permitted until. Non-null EXACTLY on `GOVERNED_WINDOW`. */
  readonly deadlineHe: string | null;
  /** What a reversal would give back. Non-null wherever a reversal is still possible at all. */
  readonly restoresHe: string | null;
  /**
   * Non-null EXACTLY on `UNDETERMINABLE`: the answer is not "no", it is unknown — and this names
   * which configuration failure made it unknown, because the two are fixed differently.
   */
  readonly unknownHe: string | null;
}

const UNDETERMINABLE_BECAUSE_HE: Readonly<Record<string, string>> = {
  retention_policy_absent:
    'לא הוגדרה מדיניות שמירה למשרד הזה, ולכן אין מסמך שאפשר לקרוא ממנו עד מתי ההחזרה מותרת. ' +
    'המערכת אינה ממציאה תקופת שמירה במקום מי שאמור לקבוע אותה.',
  retention_policy_invalid:
    'קיימת מדיניות שמירה למשרד הזה והיא אינה עומדת בפני עצמה, ולכן אי אפשר לחשב ממנה מועד. ' +
    'הפירוט של מה שאינו תקין בה מופיע להלן.',
};

/** The two configuration failures above, exposed so a test can enumerate them. */
export function mappedUndeterminableReasons(): readonly string[] {
  return Object.keys(UNDETERMINABLE_BECAUSE_HE);
}

const RESTORES_NOTHING_HE =
  'ההחזרה מחזירה את ההתקשרות בלבד ולא יחידת גישה אחת: תוכנת החיבור נרשמת מחדש, פרטי ההזדהות ' +
  'למקורות מונפקים מחדש, המשתמשים מתחברים שוב, ההזמנות נשלחות שוב, והמנוי נשאר מסתיים עד להחלטה ' +
  'מסחרית נפרדת.';

/**
 * The five members, and the reason each one has its own paragraph is in the header.
 *
 * `CLOSED` and `IRREVERSIBLE` are the pair a reader is most likely to merge, and they are the pair
 * that must not merge: in the first NOTHING HAS BEEN DESTROYED and the deletion workflow has merely
 * become entitled to run; in the second it has run.
 */
export function reversalText(reversal: Reversal): ReversalText {
  switch (reversal.kind) {
    case 'FREE':
      return {
        kind: 'FREE',
        labelHe: 'ההחזרה חופשית',
        tone: 'ok',
        explanationHe:
          'לא נשלל דבר, ולכן אין מה לשחזר. ההחזרה היא שינוי מצב אחד, והיא אינה מוגבלת בזמן.',
        deadlineHe: null,
        restoresHe: 'הפעולות נפתחות שוב בדיוק כפי שהיו, משום שדבר מהן לא נסגר מעולם.',
        unknownHe: null,
      };
    case 'GOVERNED_WINDOW':
      return {
        kind: 'GOVERNED_WINDOW',
        labelHe: 'ההחזרה אפשרית עד מועד קבוע',
        tone: 'warn',
        explanationHe:
          'ההחזרה מותרת עד המועד שמדיניות השמירה קובעת — המועד שבו תהליך המחיקה המבוקר רשאי להתחיל. ' +
          'החלון נסגר כשההשמדה נעשית אפשרית, ולא כשהיא נעשית.',
        // The instant, always. A window with no stated end is not a window.
        deadlineHe: formatInstantHe(reversal.until),
        restoresHe: RESTORES_NOTHING_HE,
        unknownHe: null,
      };
    case 'CLOSED':
      return {
        kind: 'CLOSED',
        labelHe: 'חלון ההחזרה נסגר',
        tone: 'error',
        explanationHe:
          'המועד שנקבע במדיניות השמירה חלף. שום דבר לא נמחק ולא הושמד — מה שהשתנה הוא שתהליך המחיקה ' +
          'המבוקר רשאי מעכשיו להתחיל, והחזרה אחרי הרגע הזה הייתה מתחרה בתהליך שמותר לו לרוץ.',
        deadlineHe: null,
        restoresHe: null,
        unknownHe: null,
      };
    case 'UNDETERMINABLE':
      return {
        kind: 'UNDETERMINABLE',
        labelHe: 'לא ניתן לקבוע אם ההחזרה אפשרית',
        tone: 'error',
        explanationHe:
          'זו אינה תשובה שלילית. אין כאן קביעה שההחזרה בלתי אפשרית, אלא שאין בסיס לקבוע אותה — ' +
          'והתשובה הכנה היא שהיא אינה ידועה.',
        deadlineHe: null,
        restoresHe: null,
        unknownHe: UNDETERMINABLE_BECAUSE_HE[reversal.because] ?? null,
      };
    case 'IRREVERSIBLE':
      return {
        kind: 'IRREVERSIBLE',
        labelHe: 'אין החזרה',
        tone: 'muted',
        explanationHe:
          'הנתונים הושמדו. אין החזרה משום שאין עוד מה להחזיר, וזה המצב היחיד בתהליך שבו המשפט הזה נכון.',
        deadlineHe: null,
        restoresHe: null,
        unknownHe: null,
      };
  }
}

/** `null` on `ACTIVE`, which carries no `reversal` key at all — the question does not arise. */
export function stageReversalText(stage: OffboardingStage): ReversalText | null {
  return stage.stage === 'ACTIVE' ? null : reversalText(stage.reversal);
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// WHAT TERMINATION STOPS — and what it does not touch
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export interface RevocationClassText {
  readonly classKey: RevocationClass;
  readonly labelHe: string;
  /** Why this one, and why HERE in the order. The order is the answer to "what stopped first". */
  readonly detailHe: string;
}

/**
 * The five classes IN THE SERVER'S ORDER — decreasing autonomy.
 *
 * The order is not presentation. The connector credential goes first because it is an unattended
 * process on the customer's machine that needs no session and no person: until it is dead it keeps
 * reading the customer's accounting database. The sessions go last because they are the only VISIBLE
 * revocation — evicting everyone is the moment the customer's staff find out.
 */
export const TERMINATION_REVOKES: readonly RevocationClassText[] = [
  {
    classKey: 'connectorCredentials',
    labelHe: 'אישורי ההזדהות של תוכנת החיבור',
    detailHe:
      'ראשונים, ולא במקרה: תוכנת החיבור רצה על מחשב של הלקוח בלי אדם ובלי התחברות, וכל עוד האישור ' +
      'שלה תקף היא ממשיכה לקרוא ממערכת הנהלת החשבונות שלו.',
  },
  {
    classKey: 'connectors',
    labelHe: 'תוכנות החיבור עצמן',
    detailHe:
      'שניים. מצב התוכנה הוא שורה שמישהו קורא, ואילו האישור נבדק בכל הודעה — לכן נסגר קודם מה שנמצא ' +
      'בנתיב החם.',
  },
  {
    classKey: 'sourceConnections',
    labelHe: 'ההרשאות למקורות הנתונים של הלקוח',
    detailHe:
      'שלישיים. אלה פרטי הכניסה למערכות של הלקוח עצמו, והם נסגרים אחרי תוכנת החיבור שמציגה אותם — ' +
      'זה החלק שהלקוח זכאי שייפסק, וזכאי לוודא שנפסק.',
  },
  {
    classKey: 'invitations',
    labelHe: 'ההזמנות שטרם נענו',
    detailHe:
      'רביעיות, ולפני ההתחברויות. הזמנה פתוחה בתיבת דואר יוצרת משתמש מזוהה חדש ברגע שנענית, ולכן ' +
      'סוגרים את מקור המשתמשים החדשים לפני שסוגרים את הקיימים.',
  },
  {
    classKey: 'sessions',
    labelHe: 'ההתחברויות של כל אנשי המשרד',
    detailHe:
      'אחרונות. אלה הפחות מסוכנות — מזוהות, קצרות ובנות ביטול אחת-אחת — והן היחידות שנראות: הרגע ' +
      'שבו אנשי המשרד יודעים שמשהו קרה.',
  },
];

/**
 * The sentence the whole screen exists for.
 *
 * An operator who believes "סיום" means "מחיקה" either avoids a correct action or takes a
 * catastrophic one. It is stated before the click and again beside the result.
 */
export const TERMINATION_DESTROYS_NOTHING_HE =
  'סיום התקשרות אינו מחיקה. לא נמחקת אף חברה, אף משתמש, אף הרשאה ואף רשומה — מה שנשלל הוא הגישה ' +
  'בלבד. המחיקה היא תהליך נפרד ומבוקר, עם אישור משלו, והיא אינה מתבצעת מהמסך הזה ולא מאף מסך אחר.';

export interface RevocationLine {
  readonly classKey: RevocationClass;
  readonly labelHe: string;
  readonly count: number;
}

/** What became unreachable, in the order it became unreachable. Never re-sorted. */
export function revocationLines(tally: RevocationTally): readonly RevocationLine[] {
  return TERMINATION_REVOKES.map((entry) => ({
    classKey: entry.classKey,
    labelHe: entry.labelHe,
    count: tally[entry.classKey],
  }));
}

/** Exposed for the mirror test: every class the server counts has a line and a name. */
export function mappedRevocationClasses(): readonly string[] {
  return TERMINATION_REVOKES.map((entry) => entry.classKey);
}

const MUST_REESTABLISH_HE: Readonly<Record<MustReestablishItem, string>> = {
  connector_enrolment: 'תוכנת החיבור נרשמת מחדש — פעולה גלויה ומתועדת על המחשב של הלקוח',
  source_connection_credentials: 'פרטי ההזדהות למקורות הנתונים מונפקים מחדש',
  user_sessions: 'כל אנשי המשרד מתחברים מחדש',
  invitations: 'ההזמנות שטרם נענו נשלחות שוב',
  subscription_state:
    'המנוי נשאר מסתיים — החזרת השירות היא החלטה מסחרית נפרדת, ואינה נגררת מהחזרת ההתקשרות',
};

export function mustReestablishHe(item: string): string | null {
  return MUST_REESTABLISH_HE[item as MustReestablishItem] ?? null;
}

export function mappedMustReestablishItems(): readonly string[] {
  return Object.keys(MUST_REESTABLISH_HE);
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE RETENTION POLICY
// ══════════════════════════════════════════════════════════════════════════════════════════════════

const RETENTION_VIOLATION_HE: Readonly<Record<string, string>> = {
  basis_blank: 'לא נרשם על סמך מה נקבעה המדיניות. מדיניות שאינה מציינת את מקורה אינה ניתנת לבדיקה.',
  deletion_eligibility_not_a_day_count: 'מספר הימים עד שמותר להתחיל במחיקה אינו מספר ימים תקין.',
  reinstatement_window_not_a_day_count: 'מספר הימים שבהם ההחזרה מותרת אינו מספר ימים תקין.',
  reinstatement_window_outlives_retention:
    'חלון ההחזרה נמשך מעבר למועד שבו מותר להתחיל במחיקה. שני תהליכים עם כוונות הפוכות היו רשאים ' +
    'לרוץ באותו רגע על אותם נתונים, ולכן המדיניות נדחית כפגומה ולא מוכרעת לפי מי שהספיק ראשון.',
};

/**
 * The residency half arrives with a `residency_` prefix and an open suffix from `packages/sources`,
 * so it is named as a class rather than one sentence per member — and a violation this build has no
 * words for answers `null`, which the screen writes out as an absence instead of printing the code.
 */
const RESIDENCY_VIOLATION_HE =
  'החלטת מיקום הנתונים שבמדיניות אינה תקינה, ולכן אי אפשר לומר מה בכלל תשמיד מחיקה.';

export function retentionViolationHe(violation: RetentionPolicyViolation): string | null {
  if (violation.startsWith('residency_')) return RESIDENCY_VIOLATION_HE;
  return RETENTION_VIOLATION_HE[violation] ?? null;
}

export function mappedRetentionViolations(): readonly string[] {
  return Object.keys(RETENTION_VIOLATION_HE);
}

const RESIDENCY_CATEGORY_HE: Readonly<Record<ResidencyCategory, string>> = {
  LIVE_READ: 'קריאה חיה — לא נשמר במערכת עותק של נתוני הנהלת החשבונות של הלקוח',
  CACHE: 'מטמון זמני — נשמר עותק קצר-מועד לצורך ביצועים',
  SNAPSHOT: 'צילום מצב — נשמר עותק לנקודת זמן מוצהרת',
  GLPOINT_OWNED: 'נתון שנוצר במערכת — אין לו מקור חיצוני שאפשר לחזור אליו',
};

/**
 * The server sends `describeResidency`'s sentence, which is written for a boot report. The screen
 * names the CATEGORY in Hebrew and never prints that sentence — 09 §9.1 V3.
 */
export function residencyCategoryOf(residency: string): ResidencyCategory | null {
  return RESIDENCY_CATEGORIES.find((category) => residency.startsWith(category)) ?? null;
}

export function residencyHe(residency: string): string | null {
  const category = residencyCategoryOf(residency);
  return category === null ? null : RESIDENCY_CATEGORY_HE[category];
}

export function mappedResidencyCategories(): readonly string[] {
  return Object.keys(RESIDENCY_CATEGORY_HE);
}

export interface RetentionPolicyText {
  /** Every reason the policy does not hold together, in words. Empty ⇒ it does. */
  readonly violationsHe: readonly string[];
  readonly residencyHe: string | null;
  readonly setAtHe: string | null;
  /** The day counts as sentences, because "30" beside "90" is two numbers and not two rules. */
  readonly reinstatementWindowHe: string;
  readonly deletionEligibilityHe: string;
}

export function retentionPolicyText(policy: RetentionPolicySummary): RetentionPolicyText {
  return {
    violationsHe: policy.violations
      .map(retentionViolationHe)
      .filter((line): line is string => line !== null),
    residencyHe: residencyHe(policy.residency),
    setAtHe: formatInstantHe(policy.setAt),
    reinstatementWindowHe: `ההחזרה מותרת ${policy.reinstatementWindowDays} ימים מרגע סיום ההתקשרות.`,
    deletionEligibilityHe: `תהליך המחיקה המבוקר רשאי להתחיל ${policy.deletionEligibleAfterDays} ימים מרגע סיום ההתקשרות.`,
  };
}

/**
 * The absence, written out. `null` is NOT "ללא הגבלה", and this is the sentence that keeps it from
 * being read that way.
 */
export const RETENTION_ABSENT_HE = {
  titleHe: 'לא הוגדרה מדיניות שמירה למשרד הזה',
  bodyHe:
    'אין כאן תקופת שמירה ברירת מחדל, ולא תהיה: תקופת שמירה היא שאלה משפטית על תחום שיפוט, על הסכם ' +
    'ועל חובה מקצועית, ומספר שנקבע בקוד הוא חוות דעת משפטית שאיש לא התבקש לתת. כל עוד אין מדיניות ' +
    'קריאה, סיום התקשרות למשרד הזה יידחה — וזה הסירוב שמונע מהמערכת להמציא את התשובה.',
} as const;

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// MAY THE DELETION WORKFLOW BEGIN? — an answer, and deliberately no button
// ══════════════════════════════════════════════════════════════════════════════════════════════════

const PURGE_BLOCKER_HE: Readonly<Record<PurgeBlocker, string>> = {
  not_terminated: 'ההתקשרות עם המשרד לא הסתיימה, ולכן שעון השמירה לא התחיל לרוץ.',
  already_purged: 'הנתונים כבר הושמדו בתהליך קודם.',
  retention_policy_absent: 'לא הוגדרה מדיניות שמירה, ולכן אין מועד שאפשר לקרוא ממנה.',
  retention_policy_invalid: 'מדיניות השמירה הקיימת אינה תקינה, ולכן אי אפשר לחשב ממנה מועד.',
  retention_period_running: 'תקופת השמירה עדיין רצה.',
  export_obligation_outstanding:
    'קיימת התחייבות פתוחה למסירת הנתונים ללקוח. זכות הלקוח לנתונים שלו שורדת את סיום ההתקשרות, ' +
    'ולכן היא חוסמת השמדה בלי קשר ללוח השנה.',
};

export function purgeBlockerHe(blocker: PurgeBlocker): string {
  return PURGE_BLOCKER_HE[blocker];
}

export function mappedPurgeBlockers(): readonly string[] {
  return Object.keys(PURGE_BLOCKER_HE);
}

/**
 * WHY THERE IS NO BUTTON HERE, stated on the screen rather than left as an absence.
 *
 * A missing control that nobody explains reads as a control somebody forgot. This one is deliberate:
 * the platform capability set is closed, there is no capability that authorises destroying a
 * customer's data, and therefore no route and no button. Nothing on this screen decides that — it is
 * what the product does not contain.
 */
export const PURGE_HAS_NO_ACTION_HE =
  'אין במסך הזה כפתור מחיקה, ולא בשוגג: אין במוצר פעולה שמשמידה נתונים של לקוח, משום שאין הרשאה ' +
  'שמאפשרת זאת. מה שמוצג כאן הוא תשובה בלבד — האם תהליך המחיקה המבוקר רשאי להתחיל, ואם לא, מה מונע ' +
  'ממנו. גם תשובה חיובית אינה היתר למחוק; היא רק אומרת שמצד תהליך הסיום אין עוד התנגדות.';

export interface PurgeText {
  readonly titleHe: string;
  readonly tone: Tone;
  readonly explanationHe: string;
  /** Every blocker, never the first. An operator fixing one at a time asks the same question twice. */
  readonly blockersHe: readonly string[];
  /** The instant before which the workflow may not begin. `null` when the server stated none. */
  readonly notBeforeHe: string | null;
  /** What a purge would mean for this customer's data, in the policy author's own words. */
  readonly deletionImplicationHe: string | null;
  readonly residencyHe: string | null;
}

export function purgeText(purge: PurgeEligibility): PurgeText {
  if (purge.eligible) {
    return {
      titleHe: 'מצד תהליך הסיום אין עוד התנגדות להתחלת המחיקה',
      // Not `ok`: "the objections were removed" is not good news, and a green chip over it would read
      // as an invitation.
      tone: 'warn',
      explanationHe:
        'כל התנאים שתהליך הסיום בודק מתקיימים. זו אינה הרשאה למחוק ואינה הוראה למחוק — התהליך שמבצע ' +
        'מחיקה הוא תהליך נפרד עם אישור משלו, והוא אינו קיים במוצר הזה.',
      blockersHe: [],
      notBeforeHe: formatInstantHe(purge.notBefore),
      deletionImplicationHe: purge.deletionImplication,
      residencyHe: residencyHe(purge.residency),
    };
  }
  return {
    titleHe: 'תהליך המחיקה המבוקר אינו רשאי להתחיל',
    tone: 'ok',
    explanationHe: 'להלן כל מה שמונע זאת כרגע — לא הראשון מביניהם.',
    blockersHe: purge.blockedBy.map(purgeBlockerHe),
    notBeforeHe: purge.notBefore === undefined ? null : formatInstantHe(purge.notBefore),
    deletionImplicationHe: null,
    residencyHe: null,
  };
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE EXPORT DISPOSITION — a required choice with no default
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export interface ExportDispositionText {
  readonly labelHe: string;
  readonly hintHe: string;
  /** The one extra field this answer requires, or `null`. A waiver with no reason is not a waiver. */
  readonly detailLabelHe: string | null;
}

const EXPORT_DISPOSITION_HE: Readonly<Record<ExportDispositionKind, ExportDispositionText>> = {
  REQUESTED: {
    labelHe: 'לרשום עכשיו בקשה למסירת הנתונים ללקוח',
    hintHe:
      'ההתחייבות נרשמת לפני שהגישה נשללת, כך שאם משהו בהמשך ייכשל — היא כבר על הרשומה. היא גם חוסמת ' +
      'את תהליך המחיקה כל עוד לא מומשה.',
    detailLabelHe: null,
  },
  ALREADY_DELIVERED: {
    labelHe: 'הנתונים כבר נמסרו ללקוח',
    hintHe:
      'יש לציין את מזהה המסירה. טענה שהנתונים נמסרו בלי משהו להצביע עליו אינה ניתנת לבדיקה, והיא ' +
      'נטענת בדיוק ברגע שבו הלקוח מאבד את היכולת לבדוק אותה בעצמו.',
    detailLabelHe: 'מזהה המסירה',
  },
  WAIVED: {
    labelHe: 'הלקוח ויתר על מסירת הנתונים',
    hintHe:
      'ויתור מחייב נימוק כתוב. ויתור שאינו יכול לומר מדוע הוא זכות של לקוח שנשללה בלי רישום של מי ' +
      'שלל אותה ועל סמך מה.',
    detailLabelHe: 'על סמך מה נרשם הוויתור',
  },
};

export function exportDispositionText(kind: ExportDispositionKind): ExportDispositionText {
  return EXPORT_DISPOSITION_HE[kind];
}

export function mappedExportDispositions(): readonly string[] {
  return Object.keys(EXPORT_DISPOSITION_HE);
}

/**
 * The customer's right, stated where the choice is made.
 *
 * The field has no default and the form cannot be submitted without an answer, for the route's own
 * reason: an omitted disposition would silently become a waiver, and a right that disappears when a
 * field is omitted is not a right.
 */
export const EXPORT_DISPOSITION_REQUIRED_HE =
  'זכות הלקוח לקבל את הנתונים שלו שורדת את סיום ההתקשרות, ולכן לשאלה הזו אין ערך ברירת מחדל ואין ' +
  'אפשרות לדלג עליה.';

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE TWO ACTIONS
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export const OFFBOARDING_ACTIONS = ['terminate', 'reinstate'] as const;
export type OffboardingAction = (typeof OFFBOARDING_ACTIONS)[number];

export interface OffboardingActionText {
  /** A VERB — 09 §1.3, and `<Modal>`'s `intentHe` is required to be one. */
  readonly intentHe: string;
  readonly blastRadiusHe: string;
  readonly confirmLabelHe: string;
  readonly irreversible: boolean;
  /** The written reason is mandatory on EVERY stage transition, and it is audited beside the actor. */
  readonly reasonHintHe: string;
}

const ACTION_HE: Readonly<Record<OffboardingAction, OffboardingActionText>> = {
  terminate: {
    intentHe: 'סיום ההתקשרות עם הלקוח',
    blastRadiusHe:
      'הפעולה שוללת את הגישה של המשרד במלואה — אישורי תוכנת החיבור, תוכנות החיבור, ההרשאות למקורות ' +
      'הנתונים, ההזמנות הפתוחות וכל ההתחברויות — ולאחר מכן סוגרת את הלקוח ומסיימת את המנוי. ' +
      'שום נתון אינו נמחק. ההחזרה אפשרית עד המועד שמדיניות השמירה קובעת.',
    confirmLabelHe: 'סיום ההתקשרות',
    // Access does end everywhere at once, and the window that permits reversal is finite — so the
    // control is re-selected explicitly and is never the default focus. It is NOT marked irreversible
    // because nothing is destroyed, and saying otherwise here would be the collapse this file exists
    // against.
    irreversible: true,
    reasonHintHe:
      'הסיבה נרשמת ביומן יחד עם מי שביצע את הפעולה, והיא חובה. סיום התקשרות ללא סיבה כתובה הוא ' +
      'אירוע שאיש לא יוכל להסביר בדיעבד, ולכן הבקשה אינה נשלחת בלעדיה.',
  },
  reinstate: {
    intentHe: 'החזרת ההתקשרות עם הלקוח',
    blastRadiusHe:
      'ההחזרה מחזירה את ההתקשרות ולא יחידת גישה אחת: תוכנת החיבור נרשמת מחדש, פרטי ההזדהות למקורות ' +
      'מונפקים מחדש, המשתמשים מתחברים שוב וההזמנות נשלחות שוב. המנוי נשאר מסתיים בכוונה, כך שהחזרת ' +
      'השירות נשארת החלטה מסחרית נפרדת. הפעולה אפשרית רק בתוך החלון שמדיניות השמירה קובעת.',
    confirmLabelHe: 'החזרת ההתקשרות',
    irreversible: false,
    reasonHintHe:
      'הסיבה נרשמת ביומן יחד עם מי שביצע את הפעולה, והיא חובה — כמו בסיום עצמו.',
  },
};

export function offboardingActionText(action: OffboardingAction): OffboardingActionText {
  return ACTION_HE[action];
}

/**
 * WHOSE DECISION THIS IS — stated because the alternative reads as an error.
 *
 * Reinstatement is governed by the same authority as ending the relationship, and NOT by the
 * reactivation authority that reverses a suspension. The reason is a security one: reactivation is
 * held more widely and without dual control, so naming it here would let it undo a dual-controlled
 * decision. The screen says so, because an operator who expects "ביטול השעיה" to work on a
 * terminated customer would otherwise read the refusal as a bug.
 */
export const REINSTATEMENT_AUTHORITY_HE =
  'החזרת התקשרות שהסתיימה אינה ביטול השעיה, והיא אינה נעשית באותה סמכות. הסמכות לסיים התקשרות ' +
  'והסמכות להחזירה בתוך החלון הן אותה סמכות אחת — זו שנדרשת גם לסגירת הלקוח, ובקרה כפולה בכללה. ' +
  'ביטול השעיה, לעומת זאת, מטופל במחזור החיים המסחרי ולא כאן.';

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE REFUSALS
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * The arcs another service owns, by the string the server sends.
 *
 * The value on the wire is a SERVICE AND METHOD NAME. It is actionable and it is not readable, so it
 * is translated here and never rendered — 09 §9.1 V3. `contract.mirror.test.ts` reads the arc table
 * off disk, so an arc that changes owner cannot leave a stale sentence behind.
 */
const ARC_OWNER_HE: Readonly<Record<string, string>> = {
  'CommercialService.suspendTenant': 'השעיית לקוח נעשית ממחזור החיים המסחרי של הלקוח.',
  'CommercialService.reactivateTenant':
    'ביטול השעיה נעשה ממחזור החיים המסחרי של הלקוח, ולא מתהליך הסיום.',
  'TenantLifecycleService.terminate': 'סיום ההתקשרות נעשה מהמסך הזה.',
  'TenantLifecycleService.reinstate': 'החזרת ההתקשרות נעשית מהמסך הזה.',
  'the governed deletion workflow — NOT THIS SERVICE':
    'השמדת נתונים נעשית בתהליך מחיקה מבוקר ונפרד, שאינו קיים במוצר הזה ואינו נגיש מכאן.',
};

export function arcOwnerHe(arcOwner: string): string | null {
  return ARC_OWNER_HE[arcOwner] ?? null;
}

export function mappedArcOwners(): readonly string[] {
  return Object.keys(ARC_OWNER_HE);
}

const REFUSAL_HE: Readonly<Record<string, { readonly titleHe: string; readonly explanationHe: string }>> = {
  reason_required: {
    titleHe: 'חסרה סיבה לפעולה',
    explanationHe:
      'הסיבה נבדקת לפני שנקרא משהו בכלל, ולכן בקשה ללא סיבה אינה נוגעת בדבר. שום שינוי לא בוצע.',
  },
  export_waiver_detail_required: {
    titleHe: 'חסר נימוק לוויתור על מסירת הנתונים',
    explanationHe:
      'ויתור על זכות הלקוח לנתונים שלו מחייב נימוק כתוב, כדי שיהיה רישום של מי ויתר ועל סמך מה.',
  },
  tenant_not_found: {
    titleHe: 'הלקוח אינו זמין',
    explanationHe: 'לא הצלחנו למצוא את הלקוח שאליו מפנה הפעולה.',
  },
  already_in_stage: {
    titleHe: 'הלקוח כבר בשלב הזה',
    explanationHe:
      'דיווח על סיום שכבר בוצע היה מכניס ליומן מעבר שלא קרה, ולכן זו אינה שגיאה ואינה הצלחה.',
  },
  stage_is_terminal: {
    titleHe: 'זהו שלב סופי',
    explanationHe: 'מהשלב שבו נמצא הלקוח אין מעבר לשום שלב אחר.',
  },
  illegal_stage_transition: {
    titleHe: 'המעבר אינו אפשרי מהשלב הנוכחי',
    explanationHe: 'המעבר שביקשת אינו יוצא מהשלב שבו הלקוח נמצא כרגע.',
  },
  not_terminated: {
    titleHe: 'ההתקשרות עם הלקוח לא הסתיימה',
    explanationHe: 'אין מה להחזיר: הלקוח אינו נמצא בשלב שממנו מחזירים.',
  },
  purged_is_irreversible: {
    titleHe: 'הנתונים הושמדו',
    explanationHe:
      'תהליך המחיקה המבוקר כבר רץ על המשרד הזה. אין כאן איסור על החזרה — פשוט אין עוד מה להחזיר.',
  },
  retention_policy_absent: {
    titleHe: 'לא הוגדרה מדיניות שמירה',
    explanationHe:
      'הפעולה נדחתה לפני שנשלל דבר. אין תקופת שמירה ברירת מחדל, ולכן בלי מדיניות קריאה אי אפשר ' +
      'לענות בדיעבד על אף שאלה לגבי הפעולה הזו.',
  },
  retention_policy_invalid: {
    titleHe: 'מדיניות השמירה אינה תקינה',
    explanationHe: 'הפעולה נדחתה לפני שנשלל דבר. להלן כל מה שאינו תקין במדיניות, ולא הראשון מביניהם.',
  },
  termination_instant_unrecorded: {
    titleHe: 'לא נרשם מועד סיום ההתקשרות',
    explanationHe:
      'בלי מועד סיום אי אפשר לחשב את חלון ההחזרה, וחלון שאי אפשר לחשב אינו חלון פתוח.',
  },
  reinstatement_window_closed: {
    titleHe: 'חלון ההחזרה נסגר',
    explanationHe:
      'המועד שנקבע במדיניות השמירה חלף. שום דבר לא הושמד — מה שהשתנה הוא שתהליך המחיקה המבוקר רשאי ' +
      'להתחיל, והחזרה עכשיו הייתה מתחרה בו.',
  },
  tenant_close_refused: {
    titleHe: 'סגירת הלקוח נדחתה',
    explanationHe: 'החלק המסחרי של הפעולה סורב, ולכן השלב לא הועבר.',
  },
  subscription_state_refused: {
    titleHe: 'סיום המנוי נדחה',
    explanationHe: 'החלק המסחרי של הפעולה סורב, ולכן השלב לא הועבר.',
  },
  concurrent_modification: {
    titleHe: 'מצב הלקוח השתנה בזמן הפעולה',
    explanationHe: 'מישהו אחר שינה את מצב הלקוח בין הקריאה לכתיבה, ולכן השלב לא הועבר.',
  },
};

const GENERIC_REFUSAL = {
  titleHe: 'הפעולה לא בוצעה',
  explanationHe: 'השרת סירב לבקשה.',
};

export function mappedOffboardingRefusals(): readonly string[] {
  return Object.keys(REFUSAL_HE);
}

export interface OffboardingRefusalText {
  readonly titleHe: string;
  readonly explanationHe: string;
  readonly nextStepHe: string;
  readonly tone: Tone;
  /**
   * `null` EXACTLY when the server carried a tally — because then something DID change, and
   * "nothing was saved" would be the one sentence an operator must not be given.
   */
  readonly unchangedHe: string | null;
  /** Non-null exactly when the cascade ran before the refusal. Read it before anything else. */
  readonly partialHe: string | null;
  readonly revokedLines: readonly RevocationLine[];
  readonly violationsHe: readonly string[];
  readonly currentStageHe: string | null;
  readonly allowedStagesHe: string | null;
  readonly arcOwnerHe: string | null;
  readonly reinstatableUntilHe: string | null;
  readonly notBeforeHe: string | null;
  /** The commercial half's own refusal, in the words `lifecycleText.ts` already owns. */
  readonly commercialHe: string | null;
}

const UNCHANGED_HE = 'לא נשמר שינוי. מצב הלקוח נשאר כפי שהיה לפני הבקשה.';

/**
 * THE PARTIAL. The sentence that stops an operator walking away from a tenant whose access is gone.
 *
 * The cascade has five steps and no transaction that this build can prove. If it ran and the stage
 * row then failed to move, access is ALREADY WITHDRAWN — and the server says so by putting the tally
 * on the refusal. Rendering "the server refused" over that would be a report of no change where
 * every session, credential and connection in the tenant is already unreachable.
 */
const PARTIAL_HE =
  'שים לב: שלילת הגישה כבר בוצעה. מה שלא הושלם הוא רישום השלב, ולכן המצב שמוצג בכרטיס אינו משקף ' +
  'את מה שכבר נסגר בפועל. להלן מה שנשלל, לפי הסדר שבו נשלל. הפעולה ניתנת לשליחה חוזרת — כל שלב בה ' +
  'בנוי כך שחזרה עליו אינה מוסיפה נזק.';

export function offboardingRefusalText(refusal: OffboardingRefusal): OffboardingRefusalText {
  const copy = REFUSAL_HE[refusal.reason] ?? GENERIC_REFUSAL;
  const partial = refusal.revoked !== null;

  const currentStageHe =
    refusal.currentStage === null ? null : `שלב הלקוח כעת: ${stageHe(refusal.currentStage).labelHe}.`;
  const allowedStagesHe =
    refusal.allowedStages.length === 0
      ? null
      : `מהשלב הנוכחי אפשר לעבור אל: ${refusal.allowedStages.map((s) => stageHe(s).labelHe).join(' · ')}.`;

  return {
    titleHe: copy.titleHe,
    explanationHe: copy.explanationHe,
    nextStepHe:
      allowedStagesHe ??
      'אפשר לרענן את כרטיס הלקוח ולראות את מצבו העדכני לפני ניסיון נוסף.',
    tone: refusal.reason === 'already_in_stage' ? 'warn' : 'error',
    unchangedHe: partial ? null : UNCHANGED_HE,
    partialHe: partial ? PARTIAL_HE : null,
    revokedLines: refusal.revoked === null ? [] : revocationLines(refusal.revoked),
    violationsHe: refusal.violations
      .map(retentionViolationHe)
      .filter((line): line is string => line !== null),
    currentStageHe,
    allowedStagesHe,
    arcOwnerHe: refusal.arcOwner === null ? null : arcOwnerHe(refusal.arcOwner),
    reinstatableUntilHe:
      refusal.reinstatableUntil === null ? null : formatInstantHe(refusal.reinstatableUntil),
    notBeforeHe: refusal.notBefore === null ? null : formatInstantHe(refusal.notBefore),
    // Reused rather than re-mapped: `commercial.ts`'s vocabulary already has one set of Hebrew, and a
    // second one here would be two answers to one question (family F9).
    commercialHe:
      refusal.commercialReason === null
        ? null
        : tenantLifecycleRefusalText({
            reason: refusal.commercialReason,
            currentStatus: null,
            permittedFrom: [],
          }).explanationHe,
  };
}

/** Every revocation class the server can count, named. Used by the screen and by the mirror test. */
export function revocationClassKeys(): readonly RevocationClass[] {
  return REVOCATION_CLASSES;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// READING WHAT ARRIVED — total by construction, and no screen does this for itself
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * A conflict body → the refusal shape, WITH THE STAGES VALIDATED INTO THE UNION.
 *
 * A stage this build has no Hebrew for is dropped rather than printed: 09 §9.1 V3, and a screen that
 * fell back to the code would put an English enum on a Hebrew screen the first time the server grew a
 * fifth stage. Everything else is copied across; nothing here decides anything.
 */
export function offboardingRefusalOf(body: ConflictBody): OffboardingRefusal {
  const known: readonly string[] = OFFBOARDING_STAGES;
  const stages = (values: readonly string[] | undefined): readonly OffboardingStageName[] =>
    (values ?? []).filter((value): value is OffboardingStageName => known.includes(value));

  return {
    reason: body.error,
    currentStage: stages(body.currentStage === undefined ? [] : [body.currentStage])[0] ?? null,
    allowedStages: stages(body.allowedStages),
    arcOwner: body.arcOwner ?? null,
    // The `residency_` members are an open suffix, so the cast is to the union that already admits
    // them; `retentionViolationHe` answers `null` for anything it has no words for.
    violations: (body.violations ?? []) as readonly RetentionPolicyViolation[],
    reinstatableUntil: body.reinstatableUntil ?? null,
    notBefore: body.notBefore ?? null,
    commercialReason: body.commercialReason ?? null,
    revoked: body.revoked ?? null,
  };
}

/**
 * What a reinstated tenant must re-establish, in Hebrew, from whatever the server sent.
 *
 * Total: a non-array answers empty, and an item this build has no words for is DROPPED rather than
 * printed. Empty therefore means "the server told us nothing we can render", which the screen writes
 * out — it never silently implies that a reinstatement restored access.
 */
export function mustReestablishLines(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string')
    .map(mustReestablishHe)
    .filter((line): line is string => line !== null);
}
